import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip } from '@mui/material';
import { useState } from 'react';
import { setClaimVoid } from '../../api/incentiveClaimApi';
import { handlePriceWithRM } from '../../utils/priceUtils';

const FONT = { fontFamily: "'Poppins', sans-serif" };

// Read-only trace for a sales agent's Incentive claim + admin Void/Restore.
// Payment is NOT done here — it goes through slips (generate -> approve on the
// Slips page). This shows the claim detail, the Astern-receipt gate, and status.
export default function IncentivePayoutModal({ open, onClose, row, onSaved }) {
    const [saving, setSaving] = useState(false);

    if (!row) return null;

    const isCash = row.rewardType === 'Cash';
    const received = !!row.asternReceived;
    const isVoid = row.displayStatus === 'Void';

    const handleVoid = async (voided) => {
        if (!window.confirm(voided ? 'Void this claim (exclude it from incentives)?' : 'Restore this claim?')) return;
        setSaving(true);
        try {
            await setClaimVoid({ claimId: row.claimId, voided });
            onSaved?.();
            onClose();
        } catch (e) {
            alert('Failed: ' + (e.response?.data || e.message));
        } finally { setSaving(false); }
    };

    const detail = (label, value) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed #E2E8F0' }}>
            <Typography sx={{ ...FONT, fontSize: '0.78rem', color: '#64748B' }}>{label}</Typography>
            <Typography sx={{ ...FONT, fontSize: '0.78rem', color: '#0F172A', fontWeight: 600 }}>{value}</Typography>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ ...FONT, fontWeight: 700, fontSize: '1rem' }}>Agent Incentive Claim</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    {detail('Agent', row.agentName ?? '-')}
                    {detail('Sale #', row.salesId)}
                    {detail('Customer', row.customerName ?? '-')}
                    {detail('Campaign', row.campaignName ?? '-')}
                    {detail('Contribution', `${row.contribution ?? 0} ${row.metric === 'Addon' ? 'units' : ''}`)}
                    {detail('Amount', isCash ? handlePriceWithRM(row.amount ?? 0) : (row.rewardLabel || '-'))}
                    {detail('Status', row.displayStatus ?? 'In Progress')}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, alignItems: 'center' }}>
                        <Typography sx={{ ...FONT, fontSize: '0.78rem', color: '#64748B' }}>Received from Astern?</Typography>
                        <Chip size="small" label={received ? 'Yes' : 'No'}
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, ...FONT,
                                bgcolor: received ? '#D1FAE5' : '#FEE2E2', color: received ? '#065F46' : '#991B1B' }} />
                    </Box>
                </Box>

                <Typography sx={{ ...FONT, fontSize: '0.75rem', color: '#475569', bgcolor: '#F1F5F9', p: 1, borderRadius: '8px' }}>
                    {received
                        ? 'This claim is eligible. To pay it, generate a slip on the Slips page (Incentive tab) and Approve it.'
                        : "Not eligible yet — mark this sale's Total Incentive row Received from Astern first."}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                {isVoid ? (
                    <Button onClick={() => handleVoid(false)} disabled={saving}
                        sx={{ ...FONT, textTransform: 'none', color: '#0F62CC', mr: 'auto' }}>Restore</Button>
                ) : (
                    <Button onClick={() => handleVoid(true)} disabled={saving}
                        sx={{ ...FONT, textTransform: 'none', color: '#991B1B', mr: 'auto' }}>Void</Button>
                )}
                <Button onClick={onClose} sx={{ ...FONT, textTransform: 'none', color: '#64748B' }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
